import * as Switch from '@wandb/weave/components/Switch';
import React, {useCallback} from 'react';

import {useMemoizedSelector} from '../../../../state/hooks';
import * as ViewHooks from '../../../../state/views/hooks';
import * as PanelActions from '../../../../state/views/panel/actions';
import type * as PanelTypes from '../../../../state/views/panel/types';
import {type Ref as SectionRef} from '../../../../state/views/panelBankSectionConfig/types';
import {panelViews} from '../../../PanelBank/types';
import {
  DEFAULT_SYNC_STEP,
  type EvaluationTablesConfig,
} from '../../../PanelEvaluationTables/common';
import {usePanelsSharedRefsContext} from '../../../Views/contexts/PanelsSharedRefsContext';
import {SettingsContentWrapper} from '../components/SettingsContentWrapper';
import {selectPanel} from '../media/mediaSettingsSelectors';

export type EvaluationTablesSettingsDrawerProps = {
  panelRef: PanelTypes.Ref;
  panelBankSectionConfigRef: SectionRef;
};

export const EvaluationTablesSettingsDrawer: React.FC<
  EvaluationTablesSettingsDrawerProps
> = ({panelRef, panelBankSectionConfigRef}) => {
  const partialPanel = useMemoizedSelector(makePanelSelector, panelRef);
  const {panelBankConfigRef} = usePanelsSharedRefsContext();

  const updateConfigAction = useCallback(
    (ref: PanelTypes.Ref, configUpdate: Partial<EvaluationTablesConfig>) =>
      PanelActions.updateConfig(
        ref,
        configUpdate,
        panelBankSectionConfigRef,
        panelBankConfigRef
      ),
    [panelBankSectionConfigRef, panelBankConfigRef]
  );
  const updateConfig = ViewHooks.useViewAction(panelRef, updateConfigAction);

  // Bail late so we still call hooks above unconditionally. The config cast
  // is applied here — adjacent to the runtime `viewType` check that makes it
  // safe — rather than inside the selector, so a non-eval panel can never
  // leak a mis-shaped config out of the selector.
  if (partialPanel.viewType !== panelViews.evaluationTables) {
    return null;
  }
  const config = partialPanel.config as EvaluationTablesConfig;
  const syncStep = config.syncStep ?? DEFAULT_SYNC_STEP;

  return (
    <SettingsContentWrapper
      title="Eval Table"
      header={
        <h2 className="text-sm font-semibold leading-[22px] text-wb-text-tertiary">
          PANEL SETTINGS
        </h2>
      }>
      <div className="flex flex-col gap-24">
        <div className="flex items-start justify-between gap-16">
          <div className="flex-1">
            <div className="text-base font-semibold text-wb-text-primary">
              Sync step
            </div>
            <p className="mt-4 text-sm text-wb-text-tertiary">
              When on, a single step control drives all compared runs; a run
              without the selected step shows its closest earlier step. When
              off, each eval can be independently stepped through.
            </p>
          </div>
          <Switch.Root
            checked={syncStep}
            onCheckedChange={(checked: boolean) =>
              updateConfig({syncStep: checked})
            }
            aria-label="Sync step"
            className="mt-4">
            <Switch.Thumb checked={syncStep} />
          </Switch.Root>
        </div>
      </div>
    </SettingsContentWrapper>
  );
};

// Selector mirroring the media drawer's pattern. Stays inline (no
// `createSelector` memo) because this drawer mounts/unmounts on open.
function makePanelSelector(panelRef: PanelTypes.Ref) {
  return (state: Parameters<ReturnType<typeof selectPanel>>[0]) => {
    const panel = selectPanel(panelRef)(state);
    return {
      viewType: panel.viewType,
      config: panel.config,
    };
  };
}
